import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { GroupShell } from './PublishingGroups';
import { groupSignUp, groupConfirm, groupResend, groupSignIn, groupForgotPassword, groupResetPassword } from '../../services/publishingGroups';
export { default as PublishingGroupPortal } from './PublishingGroupPortal';

const passwordPattern = '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{10,}';
const passwordHelp = 'At least 10 characters, including uppercase and lowercase letters, a number, and a symbol.';
function Feedback({ message, error }) { return message ? <p className={`my-4 text-sm ${error ? 'text-red-700' : ''}`} role={error ? 'alert' : 'status'}>{message}</p> : null; }
const pendingGroupEmailKey = 'bookish-pending-group-email';
function pendingGroupEmail() {
  try { return sessionStorage.getItem(pendingGroupEmailKey) || ''; } catch { return ''; }
}
function savePendingGroupEmail(email) {
  try {
    if (email) sessionStorage.setItem(pendingGroupEmailKey, email);
    else sessionStorage.removeItem(pendingGroupEmailKey);
  } catch { /* Verification also works when browser storage is unavailable. */ }
}
function signupError(err) {
  const messages = {
    UsernameExistsException: 'This email already has a Publishing Group account. Log in, or choose “Finish verifying an existing account.” Your Author and Reader accounts are separate.',
    CodeMismatchException: 'That code doesn’t match. Check the most recent email and try again.',
    ExpiredCodeException: 'That code has expired. Request a new code below.',
    LimitExceededException: 'Too many attempts. Please wait a little before trying again.',
    TooManyRequestsException: 'Too many attempts. Please wait a little before trying again.',
    InvalidPasswordException: `Please choose a password that meets these requirements: ${passwordHelp}`,
    UserNotFoundException: 'We couldn’t find that group account. Check your email address or create a new account.',
    NotAuthorizedException: 'We couldn’t verify this account. If you already verified your email, continue through Group login below.',
    InvalidParameterException: 'Please check your details and try again. If your email is already verified, continue through Group login below.',
  };
  return messages[err?.code || err?.name] || 'We couldn’t complete that step. Please try again, or contact Lady Liza below for help.';
}
export function PublishingGroupSignup() {
  const navigate = useNavigate();
  const [pendingEmail] = useState(pendingGroupEmail);
  const [details, setDetails] = useState({ firstName: '', lastName: '', email: pendingEmail });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [step, setStep] = useState(pendingEmail ? 'verify' : 'signup');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const lock = useRef(false);
  const stepHeading = useRef(null);
  useEffect(() => { if (step !== 'signup') stepHeading.current?.focus(); }, [step]);
  const update = field => e => setDetails({ ...details, [field]: field === 'email' ? e.target.value.trim() : e.target.value });
  function changeStep(next) {
    setMessage(''); setError(false); setCode(''); setPassword(''); setShowPassword(false); setStep(next);
  }
  async function run(label, action) {
    if (lock.current) return;
    lock.current = true;
    setBusy(label); setMessage(''); setError(false);
    try { await action(); }
    catch (err) { setMessage(signupError(err)); setError(true); }
    finally { lock.current = false; setBusy(''); }
  }
  async function submit(e) {
    e.preventDefault();
    if (lock.current || !e.currentTarget.reportValidity()) return;
    if (step === 'signup' && (!details.firstName.trim() || !details.lastName.trim())) {
      setError(true); setMessage('Please enter the group administrator’s first and last name.'); return;
    }
    if (step === 'verify' && !code.trim()) {
      setError(true); setMessage('Please enter the verification code from your email.'); return;
    }
    const email = details.email.trim().toLowerCase();
    await run(step === 'signup' ? 'Creating your account…' : step === 'verify' ? 'Verifying your email…' : 'Continuing…', async () => {
      if (step === 'signup') {
        await groupSignUp({ firstName: details.firstName.trim(), lastName: details.lastName.trim(), email, password });
        savePendingGroupEmail(email); setDetails(current => ({ ...current, email }));
        changeStep('verify'); setMessage('Check your email for your verification code.');
      } else if (step === 'resume') {
        savePendingGroupEmail(email); setDetails(current => ({ ...current, email }));
        changeStep('verify'); setMessage('Enter your latest code, or request a new one below.');
      } else {
        await groupConfirm(email, code.trim());
        savePendingGroupEmail('');
        navigate('/publishing-groups/login', { replace: true, state: { verified: true, email } });
      }
    });
  }
  return <GroupShell><section className="group-signup-page max-w-xl mx-auto px-6 py-12">
    <p className="text-sm uppercase tracking-widest font-semibold mb-3">Your shared story starts here</p>
    <h1 className="font-serif text-3xl mb-4">Create a Publishing Group</h1>
    <p className="mb-4">A shared home for your stories starts with one account for your publishing company, imprint, or collective.</p>
    <p className="mb-4">Create your account and verify your email to explore the portal first. Activate your <strong>$9.99/month group membership</strong> when you’re ready, then choose your group name and address. Author memberships are separate.</p>
    <p className="mb-6 text-sm">Use the group administrator’s details below. You can use the same email as your Author or Reader account; your Publishing Group account is separate.</p>
    <Card><h2 ref={stepHeading} tabIndex={-1} className="text-sm font-semibold mb-5">{step === 'resume' ? 'Finish verifying an existing account' : `Step ${step === 'signup' ? 1 : 2} of 2 — ${step === 'signup' ? 'Administrator details' : 'Verify your email'}`}</h2>
      <form onSubmit={submit} aria-label="Publishing Group signup" aria-busy={Boolean(busy)} className="space-y-4"><fieldset disabled={Boolean(busy)} className="space-y-4">
        {step === 'signup' ? <>
          <Input label="Administrator first name" autoComplete="given-name" required value={details.firstName} onChange={update('firstName')} pattern=".*\S.*" />
          <Input label="Administrator last name" autoComplete="family-name" required value={details.lastName} onChange={update('lastName')} pattern=".*\S.*" />
          <Input label="Email" type="email" autoComplete="email" required value={details.email} onChange={update('email')} />
          <Input label="Password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required minLength={10} pattern={passwordPattern} value={password} onChange={e => setPassword(e.target.value)} aria-describedby="password-help" />
          <p id="password-help" className="text-sm">{passwordHelp}</p>
          <label className="flex gap-2 items-center min-h-[44px]"><input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} />Show password</label>
        </> : step === 'resume' ? <>
          <p className="text-sm">Enter the email used for your Publishing Group account. You can enter an existing code or request a new one on the next step.</p>
          <Input label="Email" type="email" autoComplete="email" required value={details.email} onChange={update('email')} />
        </> : <>
          <p className="break-words">Enter the code sent to <strong>{details.email}</strong>. Check your spam folder if it hasn’t arrived.</p>
          <Input label="Verification code" required autoComplete="one-time-code" inputMode="numeric" value={code} onChange={e => setCode(e.target.value)} />
          <Button type="button" variant="outline" onClick={() => run('Sending a new code…', async () => { await groupResend(details.email); setMessage('A new code has been sent. Use the most recent email.'); })}>Resend code</Button>
        </>}
        <Feedback message={busy || message} error={!busy && error} />
        <Button type="submit" className="w-full">{busy || (step === 'signup' ? 'Create Publishing Group Account' : step === 'resume' ? 'Continue verification' : 'Verify email')}</Button>
        <Button type="button" variant="ghost" onClick={() => { savePendingGroupEmail(''); changeStep(step === 'resume' ? 'signup' : 'resume'); }}>{step === 'signup' ? 'Finish verifying an existing account' : step === 'resume' ? 'Create a new account instead' : 'Use a different email'}</Button>
      </fieldset></form>
    </Card>
    <Link className="block mt-6 underline" to="/publishing-groups/login">Already have an account? Group login</Link>
    <Link className="block mt-4 underline" to="/publishing-groups">Back to Publishing Groups</Link>
    <p className="mt-6 text-sm">Read our <Link className="underline" to="/privacy">Privacy Policy</Link> and <Link className="underline" to="/terms">Terms</Link>. Questions? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
  </section></GroupShell>;
}

function groupLoginError(err, mode) {
  const code = err?.code || err?.name;
  const messages = {
    NotAuthorizedException: mode === 'login' ? 'We couldn’t sign you in. Check your group email and password, or reset your password below.' : 'We couldn’t complete that step. Check your details, or return to login if you already verified your email.',
    UserNotFoundException: 'We couldn’t find that group account. Check your email or create a Publishing Group account below.',
    CodeMismatchException: `That code doesn’t match. Use the latest ${mode === 'reset' ? 'password-reset' : 'email-verification'} code sent to this email.`,
    ExpiredCodeException: `That code has expired. Use “${mode === 'reset' ? 'Resend reset code' : 'Resend verification code'}” below.`,
    InvalidPasswordException: `Please choose a password that meets these requirements: ${passwordHelp}`,
    LimitExceededException: 'Too many attempts. Please wait a little before trying again.',
    TooManyRequestsException: 'Too many attempts. Please wait a little before trying again.',
    InvalidParameterException: 'Please check your details and try again. Contact Lady Liza below if you need help.',
    PasswordResetRequiredException: 'Please reset your password to finish signing in.',
  };
  return messages[code] || 'We couldn’t complete that step. Please try again, or contact Lady Liza below for help.';
}

export function PublishingGroupLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => location.state?.verified && typeof location.state.email === 'string' ? location.state.email : '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(() => location.state?.verified ? 'Email verified. Sign in to explore your Publishing Group Portal.' : '');
  const [error, setError] = useState(false);
  const lock = useRef(false);
  const heading = useRef(null);
  const previousMode = useRef(mode);
  useEffect(() => {
    if (previousMode.current !== mode) heading.current?.focus();
    previousMode.current = mode;
  }, [mode]);

  function changeMode(next) {
    setPassword(''); setShowPassword(false); setCode(''); setMessage(''); setError(false); setMode(next);
  }
  async function run(label, action) {
    if (lock.current) return;
    lock.current = true;
    setBusy(label); setMessage(''); setError(false);
    try { await action(); }
    catch (err) {
      const errorCode = err?.code || err?.name;
      if (errorCode === 'UserNotConfirmedException') {
        changeMode('verify');
        savePendingGroupEmail(email.trim().toLowerCase());
        setMessage('Your group email still needs verification. Enter the code from your signup email, or request a new one below.');
      } else {
        if (errorCode === 'PasswordResetRequiredException') changeMode('forgot');
        setMessage(groupLoginError(err, mode)); setError(true);
      }
    } finally { lock.current = false; setBusy(''); }
  }
  async function submit(e) {
    e.preventDefault();
    if (lock.current || !e.currentTarget.reportValidity()) return;
    if ((mode === 'verify' || mode === 'reset') && !code.trim()) {
      setError(true); setMessage('Please enter the code from your email.'); return;
    }
    const accountEmail = email.trim().toLowerCase();
    setEmail(accountEmail);
    const labels = { login: 'Signing in…', verify: 'Verifying your email…', forgot: 'Sending your reset code…', reset: 'Resetting your password…' };
    await run(labels[mode], async () => {
      if (mode === 'login') {
        await groupSignIn(accountEmail, password);
        setPassword(''); navigate('/publishing-group-portal', { replace: true });
      } else if (mode === 'verify') {
        await groupConfirm(accountEmail, code.trim());
        savePendingGroupEmail(''); changeMode('login');
        setMessage('Email verified. Sign in to explore your Publishing Group Portal.');
      } else if (mode === 'forgot') {
        await groupForgotPassword(accountEmail); changeMode('reset');
        setMessage('If your account can be recovered, a password-reset code has been sent to your email.');
      } else {
        await groupResetPassword(accountEmail, code.trim(), password); changeMode('login');
        setMessage('Password reset. Sign in with your new password.');
      }
    });
  }
  function resend() {
    const resetting = mode === 'reset';
    run(resetting ? 'Sending a new reset code…' : 'Sending a new verification code…', async () => {
      if (resetting) await groupForgotPassword(email.trim().toLowerCase());
      else await groupResend(email.trim().toLowerCase());
      setCode('');
      setMessage(resetting ? 'If your account can be recovered, a new password-reset code has been sent. Use the most recent email.' : 'A new email-verification code has been sent. Use the most recent email.');
    });
  }
  const codeStep = mode === 'verify' || mode === 'reset';
  return <GroupShell><section className="group-login-page max-w-lg mx-auto px-6 py-12">
    <p className="text-sm uppercase tracking-widest font-semibold mb-3">Welcome back to your shared story</p>
    <h1 ref={heading} tabIndex={-1} className="font-serif text-3xl mb-4">{mode === 'login' ? 'Publishing Group Login' : mode === 'verify' ? 'Verify your group email' : mode === 'forgot' ? 'Request a password reset' : 'Choose a new password'}</h1>
    <p className="mb-6 text-sm">Sign in with the group administrator’s Publishing Group account. Your Author and Reader accounts are separate, even if they use the same email.</p>
    <Card><form onSubmit={submit} aria-label="Publishing Group login" aria-busy={Boolean(busy)}><fieldset disabled={Boolean(busy)} className="space-y-4">
      {codeStep ? <p className="text-sm break-words">{mode === 'reset' ? 'Use the password-reset code requested for' : 'Use the email-verification code from signup for'} <strong>{email}</strong>. Check your spam folder if it hasn’t arrived.</p> : <Input label="Email" type="email" required autoComplete="email" value={email} onChange={e => setEmail(e.target.value.trim())} />}
      {mode === 'forgot' && <p className="text-sm">Enter your group account email. We’ll send instructions for choosing a new password if the account can be recovered.</p>}
      {codeStep && <Input label={mode === 'reset' ? 'Password-reset code' : 'Email-verification code'} required autoComplete="one-time-code" inputMode="numeric" value={code} onChange={e => setCode(e.target.value)} />}
      {(mode === 'login' || mode === 'reset') && <>
        <Input label={mode === 'reset' ? 'New password' : 'Password'} type={showPassword ? 'text' : 'password'} required autoComplete={mode === 'reset' ? 'new-password' : 'current-password'} minLength={mode === 'reset' ? 10 : undefined} pattern={mode === 'reset' ? passwordPattern : undefined} aria-describedby={mode === 'reset' ? 'group-reset-password-help' : undefined} value={password} onChange={e => setPassword(e.target.value)} />
        {mode === 'reset' && <p id="group-reset-password-help" className="text-sm">{passwordHelp}</p>}
        <label className="flex gap-2 items-center min-h-[44px]"><input type="checkbox" checked={showPassword} onChange={e => setShowPassword(e.target.checked)} />Show password</label>
      </>}
      <Feedback message={busy || message} error={!busy && error} />
      <Button type="submit" className="w-full">{busy || (mode === 'login' ? 'Sign in' : mode === 'verify' ? 'Verify email' : mode === 'forgot' ? 'Send reset code' : 'Set new password')}</Button>
      {codeStep && <>
        <Button type="button" variant="outline" onClick={resend}>{mode === 'reset' ? 'Resend reset code' : 'Resend verification code'}</Button>
        <Button type="button" variant="ghost" onClick={() => { const next = mode === 'reset' ? 'forgot' : 'login'; setEmail(''); changeMode(next); }}>Use a different email</Button>
      </>}
      <Button type="button" variant="ghost" onClick={() => changeMode(mode === 'login' ? 'forgot' : 'login')}>{mode === 'login' ? 'Forgot password?' : 'Back to login'}</Button>
    </fieldset></form></Card>
    <Link className="block mt-6 underline" to="/publishing-groups/signup">Create a Publishing Group account</Link>
    <Link className="block mt-4 underline" to="/publishing-groups">Back to Publishing Groups</Link>
    <p className="mt-4 text-sm">Need a hand? <a className="underline" href="mailto:author@hayleyraejohnson.com">Contact Lady Liza</a>.</p>
  </section></GroupShell>;
}
